import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site-shell";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — DealDraft" },
      {
        name: "description",
        content: "The terms governing your use of DealDraft.",
      },
    ],
    links: [{ rel: "canonical", href: "https://dealdraft.pro/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <SiteShell>
      <article className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Terms of Service</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: August 10, 2026</p>

        <div className="mt-8 space-y-10 text-base leading-relaxed text-muted-foreground">
          <p>
            DealDraft (dealdraft.pro) is operated by <strong className="text-foreground">LJ
            Legacy Solutions LLC</strong>, a Kansas limited liability company ("DealDraft,"
            "we," "us," or "our"). These Terms of Service ("Terms") govern your use of the
            DealDraft service. Questions can be sent to{" "}
            <a
              href="mailto:support@dealdraft.pro"
              className="font-medium text-foreground underline hover:no-underline"
            >
              support@dealdraft.pro
            </a>
            .
          </p>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Acceptance of Terms
            </h2>
            <p className="mt-4">
              By creating an account or otherwise using DealDraft, you agree to be bound by
              these Terms. If you do not agree, do not use the service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              The Service
            </h2>
            <p className="mt-4">
              DealDraft is an AI-powered tool that generates proposal and contract documents
              for freelancers based on information you provide.
            </p>
          </section>

          <section className="rounded-xl border border-border bg-secondary/40 p-6">
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              AI-Generated Content; Not Legal Advice
            </h2>
            <p className="mt-4">
              Documents produced by DealDraft are AI-generated templates provided for your
              convenience only. DealDraft is not a law firm, does not provide legal advice,
              and no attorney-client relationship is formed by using the service. You are
              solely responsible for reviewing, editing, and confirming that any generated
              document is accurate, valid, and appropriate for your specific situation. You
              should consult a licensed attorney for legal matters.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Account Responsibilities
            </h2>
            <p className="mt-4">
              You are responsible for maintaining the confidentiality of your account
              credentials and for all activity that occurs under your account. Notify us
              promptly at{" "}
              <a
                href="mailto:support@dealdraft.pro"
                className="font-medium text-foreground underline hover:no-underline"
              >
                support@dealdraft.pro
              </a>{" "}
              if you suspect unauthorized use of your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Acceptable Use
            </h2>
            <p className="mt-4">
              You agree not to use DealDraft for any unlawful purpose, to generate
              fraudulent or deceptive documents, to attempt to disrupt or reverse-engineer
              the service, or to interfere with other users' access to DealDraft.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Subscription and Billing
            </h2>
            <p className="mt-4">
              DealDraft offers a Free tier (3 generations per month) and a Pro plan ($19 per
              month), billed through Stripe. You may cancel your Pro subscription at any time
              to stop future billing; cancellation takes effect at the end of the current
              billing period. Charges already made for the current billing period are final
              and non-refundable.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Intellectual Property
            </h2>
            <p className="mt-4">
              You own the proposal and contract documents you generate using DealDraft.
              LJ Legacy Solutions LLC owns the DealDraft platform, including its software,
              design, and branding.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Disclaimer of Warranties
            </h2>
            <p className="mt-4">
              DealDraft is provided "as is" and "as available," without warranties of any
              kind, express or implied, including warranties of merchantability, fitness for
              a particular purpose, or non-infringement.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Limitation of Liability
            </h2>
            <p className="mt-4">
              To the maximum extent permitted by law, LJ Legacy Solutions LLC will not be
              liable for any indirect, incidental, special, consequential, or punitive
              damages arising from your use of DealDraft, and our total liability for any
              claim will not exceed the amount you paid us in the twelve months preceding the
              claim.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Indemnification
            </h2>
            <p className="mt-4">
              You agree to indemnify and hold LJ Legacy Solutions LLC harmless from any
              claims, damages, or expenses arising from your use of DealDraft or the
              documents you generate, distribute, or rely upon.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Termination
            </h2>
            <p className="mt-4">
              We may suspend or terminate your account if you violate these Terms. You may
              stop using DealDraft and close your account at any time.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Governing Law
            </h2>
            <p className="mt-4">
              These Terms are governed by the laws of the State of Kansas, without regard to
              its conflict-of-laws principles. Any dispute arising from these Terms or your
              use of DealDraft will be resolved exclusively in the state or federal courts
              located in Kansas.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Changes to These Terms
            </h2>
            <p className="mt-4">
              We may update these Terms from time to time. If we make material changes, we
              will update the "Last updated" date at the top of this page.
            </p>
          </section>
        </div>
      </article>
    </SiteShell>
  );
}
